import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError, takeSessionMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notice] = useState(() => takeSessionMessage());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(apiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-5 py-12" style={{ backgroundColor: "#FAF5F4" }}>
      <div className="w-full max-w-[400px]">
        <div className="flex flex-col items-center text-center">
          <img
            src="/brand/heels-and-tea-logo.jpg"
            alt="Heels & Tea"
            data-testid="login-logo"
            className="h-28 w-auto mix-blend-multiply sm:h-32"
          />
          <p className="mt-3 text-base font-medium tracking-[0.02em]" style={{ color: "#776D6B" }}>
            Internal Management System
          </p>
          <p className="mt-2.5 text-sm italic tracking-wide" style={{ color: "#B66F7A" }} data-testid="login-tagline">
            Growing Together, Achieving More.
          </p>
        </div>

        <div
          className="mt-8 rounded-2xl bg-white p-7 sm:p-8"
          style={{ border: "1px solid #EEDCE3", boxShadow: "0 8px 30px -18px rgba(73,61,59,0.25)" }}
        >
          <h2 className="font-heading text-xl font-extrabold tracking-tight" style={{ color: "#493D3B" }}>
            Welcome
          </h2>
          <p className="mt-1 text-sm" style={{ color: "#776D6B" }}>
            Sign in to your workspace.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: "#776D6B" }}>
                Email Address
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                className="h-12 rounded-xl bg-white transition-all focus-visible:ring-2 focus-visible:ring-offset-0"
                style={{ borderColor: "#E7D8DC", color: "#493D3B", "--tw-ring-color": "#EBCDD7" }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: "#776D6B" }}>
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                className="h-12 rounded-xl bg-white transition-all focus-visible:ring-2 focus-visible:ring-offset-0"
                style={{ borderColor: "#E7D8DC", color: "#493D3B", "--tw-ring-color": "#EBCDD7" }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {notice && !error && (
              <p data-testid="login-session-notice" className="rounded-xl border px-3 py-2 text-sm font-medium" style={{ borderColor: "#E8CFCB", backgroundColor: "#FBEFEC", color: "#8A5A61" }}>
                {notice}
              </p>
            )}
            {error && (
              <p data-testid="login-error" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
                {error}
              </p>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="h-12 w-full rounded-xl text-base font-semibold text-white transition-all hover:brightness-105 active:scale-[0.99]"
              style={{ backgroundColor: "#C9828A" }}
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
